import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Shield, Check } from 'lucide-react';
import { mastersApi } from '../services/api';
import { Table, Modal, Field, Spinner } from '../components/common';
import toast from 'react-hot-toast';

export function Roles() {
  const [roles,   setRoles]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm,setShowForm]= useState(false);
  const [editRow, setEditRow] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await mastersApi.roles(); setRoles(r.data||[]); }
    catch {} finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cols = [
    { key:'name', title:'Role Name', render:v=><span style={{fontWeight:600}}>{v}</span> },
    { key:'code', title:'Code',      render:v=><span style={{fontFamily:'monospace',fontSize:'var(--fs-xs)',background:'var(--bg)',padding:'2px 6px',borderRadius:4}}>{v}</span> },
    { key:'description', title:'Description', render:v=><span style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)'}}>{v||'—'}</span> },
    { key:'permissions', title:'Permissions', render:v=><span style={{fontSize:'var(--fs-xs)',color:'var(--text-muted)'}}>{(v||[]).filter(Boolean).length} granted</span> },
    { key:'is_active',   title:'Status',      render:v=><span className={`badge ${v?'badge-green':'badge-red'}`}>{v?'Active':'Inactive'}</span> },
    { key:'id', title:'', align:'right', render:(_,r)=><button className="btn btn-ghost btn-sm" onClick={()=>{setEditRow(r);setShowForm(true);}}>Edit</button> },
  ];

  return (
    <div>
      <div className="page-header">
        <div><h1 className="page-title">Roles & Permissions</h1><p className="page-sub">Define access control roles with fine-grained permissions</p></div>
        <div className="page-actions"><button className="btn btn-primary" onClick={()=>{setEditRow(null);setShowForm(true);}}><Plus size={14}/> Add Role</button></div>
      </div>
      <div className="card">
        <Table columns={cols} data={roles} loading={loading} emptyText="No roles defined" />
      </div>
      <RoleModal open={showForm} onClose={()=>setShowForm(false)} editData={editRow} onSuccess={()=>{setShowForm(false);load();}} />
    </div>
  );
}

function RoleModal({ open, onClose, editData, onSuccess }) {
  const [form,        setForm]        = useState({ name:'', code:'', description:'' });
  const [permissions, setPermissions] = useState({});
  const [selected,    setSelected]    = useState(new Set());
  const [loading,     setLoading]     = useState(false);

  useEffect(() => {
    if (!open) return;
    setForm(editData ? { name:editData.name, code:editData.code, description:editData.description||'' } : { name:'', code:'', description:'' });
    mastersApi.permissions().then(r => { setPermissions(r.data||{}); });
    if (editData?.permissions) {
      setSelected(new Set(editData.permissions.filter(Boolean).map(p => `${p.module}:${p.sub_module}:${p.action}`)));
    } else {
      setSelected(new Set());
    }
  }, [open, editData]);

  const togglePerm = (permStr) => setSelected(s => { const n = new Set(s); n.has(permStr) ? n.delete(permStr) : n.add(permStr); return n; });

  const toggleModule = (module, perms) => {
    const allPerms = Object.values(perms).flat().map(p=>`${p.module}:${p.sub_module}:${p.action}`);
    const allSelected = allPerms.every(p => selected.has(p));
    setSelected(s => {
      const n = new Set(s);
      allPerms.forEach(p => allSelected ? n.delete(p) : n.add(p));
      return n;
    });
  };

  const save = async () => {
    if (!form.name || !form.code) { toast.error('Name and code required'); return; }
    setLoading(true);
    try {
      const idByStr = {};
      Object.values(permissions).forEach(subMods =>
        Object.values(subMods).flat().forEach(p => { idByStr[`${p.module}:${p.sub_module}:${p.action}`] = p.id; })
      );
      const permission_ids = [...selected].map(pStr => idByStr[pStr]).filter(Boolean);
      if (editData) {
        await mastersApi.updateRole(editData.id, { ...form, permission_ids });
      } else {
        await mastersApi.createRole({ ...form, permission_ids });
      }
      toast.success('Role saved');
      onSuccess();
    } catch {} finally { setLoading(false); }
  };

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const MODULE_LABELS = { masters:'Masters', assets:'Assets', transactions:'Transactions', depreciation:'Depreciation', audit:'Audit', reports:'Reports', dashboard:'Dashboard',
    capex:'CAPEX', users:'Users & Roles', alerts:'Alerts', support:'Support', checkouts:'Check-In/Out (legacy)', disposal:'Disposal (legacy)', insurance:'Insurance (legacy)', maintenance:'Maintenance (legacy)', transfers:'Transfers (legacy)' };
  const ACTION_COLORS = { view:'badge-blue', create:'badge-green', edit:'badge-amber', delete:'badge-red', approve:'badge-purple', export:'badge-teal' };

  return (
    <Modal open={open} onClose={onClose} title={editData ? 'Edit Role' : 'Add Role'} size="xl"
      footer={<><button className="btn btn-secondary" onClick={onClose}>Cancel</button><button className="btn btn-primary" onClick={save} disabled={loading}>{loading?<Spinner size="sm"/>:'Save Role'}</button></>}>
      <div className="form-row cols-3" style={{marginBottom:16}}>
        <Field label="Role Name" required><input className="form-control" value={form.name} onChange={set('name')} placeholder="e.g. Asset Manager" /></Field>
        <Field label="Code" required><input className="form-control" value={form.code} onChange={set('code')} style={{textTransform:'uppercase'}} placeholder="ASSET_MGR" /></Field>
        <Field label="Description"><input className="form-control" value={form.description} onChange={set('description')} /></Field>
      </div>

      <p style={{fontSize:'var(--fs-sm)',fontWeight:600,marginBottom:12,display:'flex',alignItems:'center',gap:6}}><Shield size={15} color="var(--primary)"/> Permission Matrix <span style={{fontWeight:400,color:'var(--text-muted)',fontSize:'var(--fs-xs)'}}>{selected.size} permissions selected</span></p>

      {Object.entries(permissions).map(([module, subMods]) => {
        const allPerms = Object.values(subMods).flat();
        const allPermStrs = allPerms.map(p=>`${p.module}:${p.sub_module}:${p.action}`);
        const allSel = allPermStrs.every(p=>selected.has(p));
        return (
          <div key={module} style={{marginBottom:12,border:'1px solid var(--border-light)',borderRadius:'var(--r-md)',overflow:'hidden'}}>
            <div style={{background:'var(--bg)',padding:'8px 14px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontWeight:600,fontSize:'var(--fs-sm)'}}>{MODULE_LABELS[module]||module}</span>
              <label style={{display:'flex',alignItems:'center',gap:6,cursor:'pointer',fontSize:'var(--fs-xs)',color:'var(--text-muted)'}}>
                <input type="checkbox" checked={allSel} onChange={()=>toggleModule(module, subMods)} /> Select all
              </label>
            </div>
            <div style={{padding:'10px 14px'}}>
              {Object.entries(subMods).map(([subMod, perms]) => (
                <div key={subMod} style={{display:'flex',gap:6,alignItems:'center',flexWrap:'wrap',marginBottom:6}}>
                  <span style={{fontSize:'var(--fs-xs)',color:'var(--text-secondary)',minWidth:140,textTransform:'capitalize'}}>{subMod.replace(/_/g,' ')}</span>
                  {perms.map(p => {
                    const pStr = `${p.module}:${p.sub_module}:${p.action}`;
                    const isSel = selected.has(pStr);
                    return (
                      <label key={pStr} style={{display:'flex',alignItems:'center',gap:4,cursor:'pointer',padding:'3px 8px',borderRadius:20,border:`1px solid ${isSel?'var(--primary)':'var(--border)'}`,background:isSel?'var(--primary-light)':'var(--white)',fontSize:'var(--fs-xs)',color:isSel?'var(--primary)':'var(--text-secondary)'}}>
                        <input type="checkbox" style={{display:'none'}} checked={isSel} onChange={()=>togglePerm(pStr)} />
                        {isSel && <Check size={10}/>}
                        {p.action}
                      </label>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </Modal>
  );
}

export default Roles;
